/**
 * scoreadmin-fe permission namespace + constants (AUTHZ.md §5.4), card #2415.
 *
 * Follows the score-fe/src/permissions.js precedent: derive role/permission
 * checks from score-common's manifest (score-common/permissions) rather than
 * duplicating a literal, so a future role grant is picked up automatically.
 * scoreadmin-fe's own ScoreEntitlementGate.jsx already reads the access
 * token's `roles` claim directly instead of common-fe's usePermission() /
 * PermissionProvider, for the same reason score-fe's header explains:
 * account-api's pre-token-generation.js emits no `permissions` claim at all
 * (#276 never landed), so usePermission() fails closed for every real,
 * non-super user. hasAssignScribesPermission below matches that pattern.
 */
import { manifest } from "score-common/permissions";

export const PERMS = Object.freeze({
  ASSIGN_SCRIBES: "assign_scribes",
  // #2862 — the placed-class lock's admin-only override (change placings on
  // a class whose placings are already set). Same manifest-derived pattern
  // as assign_scribes below, never an inline role string.
  OVERRIDE_PLACED_CLASS: "override_placed_class",
  // #2816 — switch which of two contender sheets for a judge seat is
  // authoritative. Same manifest-derived pattern, never an inline role
  // string. AC4: a scribe holds neither this nor assign_scribes, so a
  // scribe-only token never even shows the switch button.
  SWITCH_AUTHORITATIVE_SCORE_SHEET: "switch_authoritative_score_sheet",
  // #3009 — resolve a same-field stale_write conflict (keep_current or
  // apply_attempted) raised on the review queue. Same manifest-derived
  // pattern, score_admin only (never score_scribe, never score_judge —
  // decided 2026-09-20: the tent resolves it, never the scribe who typed the
  // value). Drives whether ReviewQueue.jsx shows the two resolve buttons on
  // a stale_write row at all.
  RESOLVE_STALE_WRITE_CONFLICT: "resolve_stale_write_conflict",
  // #3477 - pick a whole sheet version from the Review Queue. Same
  // manifest-derived pattern, score_admin only.
  PICK_SHEET_VERSION: "pick_sheet_version",
  // #3356 - replay a quarantined scribe payload through the normal validated
  // write path. Same manifest-derived pattern, score_admin only. Drives
  // whether ReviewQueue.jsx shows the Apply button on a quarantined row.
  APPLY_QUARANTINED_PAYLOAD: "apply_quarantined_payload",
  // #3571 - import a scribe tablet's saved sheets file. Same manifest-derived
  // pattern, score_admin only. Drives whether the Review Queue shows Import sheets.
  IMPORT_TABLET_SHEETS: "import_tablet_sheets",
  // #2801 - reopen (un-final) a ride's frozen sheets so its outcome can be
  // corrected. Same manifest-derived pattern, score_admin only (never a
  // scribe or judge). Drives whether RideEditor.jsx shows "Reopen ride".
  UNFINALIZE_SCORE_SHEET: "unfinalize_score_sheet",
});

// Roles whose manifest permissions include a given short key — derived from
// the manifest, not hardcoded, so a future grant to another role is picked up
// automatically.
function rolesWithPermission(permissionKey) {
  return Object.freeze(
    Object.entries(manifest.roles)
      .filter(([, role]) => role.permissions.includes(permissionKey))
      .map(([key]) => key)
  );
}

// Roles holding assign_scribes — currently just score_admin
// (packages/score-common/src/permissions/index.js).
export const ASSIGN_SCRIBES_ROLES = rolesWithPermission(PERMS.ASSIGN_SCRIBES);

// #2862 — roles holding override_placed_class (currently just score_admin).
export const OVERRIDE_PLACED_CLASS_ROLES = rolesWithPermission(PERMS.OVERRIDE_PLACED_CLASS);

// #2816 — roles holding switch_authoritative_score_sheet (currently just
// score_admin — see packages/score-common/src/permissions/index.js).
export const SWITCH_AUTHORITATIVE_SCORE_SHEET_ROLES = rolesWithPermission(
  PERMS.SWITCH_AUTHORITATIVE_SCORE_SHEET
);

// #3009 — roles holding resolve_stale_write_conflict (currently just
// score_admin — see packages/score-common/src/permissions/index.js).
export const RESOLVE_STALE_WRITE_CONFLICT_ROLES = rolesWithPermission(
  PERMS.RESOLVE_STALE_WRITE_CONFLICT
);

// #3356 - roles holding apply_quarantined_payload (currently just
// score_admin - see packages/score-common/src/permissions/index.js).
export const APPLY_QUARANTINED_PAYLOAD_ROLES = rolesWithPermission(PERMS.APPLY_QUARANTINED_PAYLOAD);

// #3571 - roles holding import_tablet_sheets (currently just score_admin).
export const IMPORT_TABLET_SHEETS_ROLES = rolesWithPermission(PERMS.IMPORT_TABLET_SHEETS);

// #2801 - roles holding unfinalize_score_sheet (currently just score_admin -
// see packages/score-common/src/permissions/index.js).
export const UNFINALIZE_SCORE_SHEET_ROLES = rolesWithPermission(PERMS.UNFINALIZE_SCORE_SHEET);

// #3478 - roles holding pick_sheet_version (currently just score_admin - see
// packages/score-common/src/permissions/index.js).
export const PICK_SHEET_VERSION_ROLES = rolesWithPermission(PERMS.PICK_SHEET_VERSION);

/**
 * Pure predicate: does this user hold score:assign_scribes for the active
 * org? `rolesForOrg` is the decoded token's `roles[activeOrgId]` array
 * (qualified "score:score_admin" or bare "score_admin" - same two forms
 * ScoreEntitlementGate.jsx's activeOrgHasScoreAdminRole matches).
 *
 * #3414: a super admin holds EVERY Score Admin capability in EVERY org, so
 * each predicate takes `isSuper` (common-fe's useActiveOrg().isSuper, decoded
 * from the token's `custom:super` / `super` claim) and returns true for it
 * regardless of `rolesForOrg`. This matches score-api's requirePermission,
 * which already lets a super through; before this the FE and API disagreed
 * and a super with no membership role saw the controls disabled.
 */
function holdsAnyRole(rolesForOrg, allowedRoles, isSuper = false) {
  if (isSuper === true) return true;
  const roles = Array.isArray(rolesForOrg) ? rolesForOrg : [];
  return roles.some(
    (r) => allowedRoles.includes(r) || allowedRoles.includes(r?.split(":").pop())
  );
}

export function hasAssignScribesPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, ASSIGN_SCRIBES_ROLES, isSuper);
}

/**
 * #2862 — does this user hold score:override_placed_class for the active org
 * (i.e. may they change placings on a class whose placings are already set)?
 * Same predicate as hasAssignScribesPermission above (super bypass included). Drives whether the Placings
 * screen offers the explicit "Unlock to edit" override on a locked class.
 */
export function hasOverridePlacedClassPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, OVERRIDE_PLACED_CLASS_ROLES, isSuper);
}

/**
 * #2816 — does this user hold score:switch_authoritative_score_sheet for the
 * active org (i.e. may they promote a contender sheet to authoritative)?
 * Same predicate as the two checks above. Drives whether RideEditor's contender-compare dialog
 * offers the "Make authoritative" action at all (AC4: a scribe never sees
 * it, let alone can click it).
 */
export function hasSwitchAuthoritativeScoreSheetPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, SWITCH_AUTHORITATIVE_SCORE_SHEET_ROLES, isSuper);
}

/**
 * #3009 — does this user hold score:resolve_stale_write_conflict for the
 * active org (i.e. may they resolve a same-field stale_write conflict raised
 * on the review queue)? Same predicate as the checks
 * above. Drives whether RideEditor's sheet-versions compare lets the user pick
 * a version to keep — a scribe-only token never even shows the choice.
 */
export function hasResolveStaleWriteConflictPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, RESOLVE_STALE_WRITE_CONFLICT_ROLES, isSuper);
}

/**
 * #2801 - does this user hold score:unfinalize_score_sheet for the active org
 * (i.e. may they reopen a final ride)? Same literal-roles-claim predicate as
 * the checks above. Drives whether RideEditor.jsx
 * shows the "Reopen ride" button at all.
 */
export function hasUnfinalizeScoreSheetPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, UNFINALIZE_SCORE_SHEET_ROLES, isSuper);
}

/**
 * #3356 - does this user hold score:apply_quarantined_payload for the active
 * org? Same predicate as the checks above. Drives whether
 * the Review Queue's quarantined row shows its Apply button.
 */
export function hasApplyQuarantinedPayloadPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, APPLY_QUARANTINED_PAYLOAD_ROLES, isSuper);
}

/** #3571 - does this user hold score:import_tablet_sheets for the active org? */
export function hasImportTabletSheetsPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, IMPORT_TABLET_SHEETS_ROLES, isSuper);
}

/**
 * #3478 - does this user hold score:pick_sheet_version for the active org
 * (i.e. may they pick a whole sheet version from the Review Queue's Sheet
 * versions screen)? Same literal-roles-claim predicate as the checks above.
 * Drives whether the screen offers the pick buttons at all; the server gates
 * POST /scoresheet/:id/pick-version the same way.
 */
export function hasPickSheetVersionPermission(rolesForOrg, isSuper = false) {
  return holdsAnyRole(rolesForOrg, PICK_SHEET_VERSION_ROLES, isSuper);
}
